import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "./authContext";
import axios from "./lib/axios";

const CartContext = createContext(null);


export const CartProvider = ({ children }) => {
  const { isLoggedIn, user, accessToken: token } = useAuth();
  const userId = user?.id || user?._id;
  const [cartItems, setCartItems] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const productCategories = {
    radio: "radio-ads",
    cinema: "cinema-ads",
    transit: "transit-ads",
    outdoor: "outdoor-items",
    "non-traditional": "non-traditional-ads",
    btl: "btl-ads",
    digital: "digital-ads",
    "digital-marketing": "digital-marketing",
    "influencer-marketing": "influencer-ads",
    "brand-endorsement": "brand-endorsements",
    designing: "designing",
    "printing": "printing",
  };
  
  // Utility function to calculate cart totals
  const calculateCartTotals = (cartItems) => {
    const validItems = Array.isArray(cartItems)
      ? cartItems.filter(Boolean)
      : [];
    const totalAmount = validItems.reduce(
      (sum, item) => sum + Number(item?.total_amount || 0),
      0,
    );
    const cartCount = validItems.length;
    return { totalAmount, cartCount };
  };
  
  // Utility function to update cart state
  const updateCartState = useCallback((items) => {
    const validItems = Array.isArray(items) ? items.filter(Boolean) : [];
    setCartItems(validItems);
    const { totalAmount } = calculateCartTotals(validItems);
    setTotalAmount(totalAmount);
  }, []);
  
  // Update auth header when token changes
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    }
  }, [token]);

  const fetchCartItems = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      const response = await axios.get(`/api/cart`, {
        params: {
          where: {
            user: {
              equals: userId,
            },
          },
        },
      });

      const cart = response.data.docs || [];
      updateCartState(cart);
    } catch (err) {
      setError(err.response?.data?.message || "Error fetching cart items");
      console.error("Error fetching cart:", err);
    } finally {
      setLoading(false);
    }
  }, [userId, updateCartState]);

  // Fetch cart items when user logs in
  useEffect(() => {
    if (isLoggedIn && user) {
      fetchCartItems();
    } else {
      updateCartState([]);
    }
  }, [isLoggedIn, user, fetchCartItems, updateCartState]);

  const addToCart = async (productId, category, cartData) => {
    if (!isLoggedIn || !user) return;

    try {
      setLoading(true);
      setError(null);
      const productCategory = productCategories[category];
      if (!productCategory) {
        throw new Error("Invalid product category");
      }

      const response = await axios.post("/api/cart", {
        user: userId,
        name: cartData.name,
        description: cartData.description,
        image_url: cartData.image_url,
        quantity: cartData.quantity,
        subtotal: cartData.subtotal,
        tax_amount: cartData.tax_amount,
        total_amount: cartData.total_amount,
        discount_amount: cartData.discount_amount,
        created_at: new Date(),
        updated_at: new Date(),
      });

      if (response.data) {
        const createdCartItem = response.data.doc || response.data;
        const updatedCart = [...cartItems, createdCartItem];
        updateCartState(updatedCart);
        return createdCartItem;
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error adding item to cart");
      console.error("Error adding to cart:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (cartId) => {
    if (!isLoggedIn || !user) return;

    try {
      setLoading(true);
      await axios.delete(`/api/cart/${cartId}`);
      const updatedCart = cartItems.filter(item => item.id !== cartId);
      updateCartState(updatedCart);
    } catch (err) {
      setError(err.response?.data?.message || "Error removing item from cart");
      console.error("Error removing from cart:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (cartId, newQuantities) => {
    if (!isLoggedIn || !user) return;

    try {
      setLoading(true);
      const response = await axios.patch(`/api/cart/${cartId}`, {
        quantity: newQuantities,
        updated_at: new Date(),
      });

      if (response.data) {
        const updatedCart = cartItems.map(item => 
          item.id === cartId ? { ...item, quantity: newQuantities } : item
        );
        updateCartState(updatedCart);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Error updating cart quantity");
      console.error("Error updating quantity:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = async (cartId) => {
    if (!isLoggedIn || !user) return;

    try {
      setLoading(true);
      await axios.delete(`/api/cart/${cartId}`);
      updateCartState([]);
    } catch (err) {
      setError(err.response?.data?.message || "Error clearing cart");
      console.error("Error clearing cart:", err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const { cartCount } = calculateCartTotals(cartItems);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        totalAmount,
        loading,
        error,
      cartCount,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      fetchCartItems,
    }}
  >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
